import { Component, OnDestroy, OnInit } from '@angular/core';
import { NbSidebarService, NbToastrService } from '@nebular/theme';
import { bufferTime, catchError, distinctUntilChanged, filter, map, skip, skipWhile, startWith, switchMap, takeUntil, } from 'rxjs/operators';
import { combineLatest, of, Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import * as fromStore from '../../../store';
import { DialogService } from 'primeng/dynamicdialog';
import { DateWithTimezoneApplied } from '../../../evja/utils/date';
import { isMultipleTimezonesError, shouldNotifyTimezoneError, TIMEZONE_TOAST_DURATION_MS } from '../../../evja/utils/graphql-errors';
import { AnalyticsService } from '../../../@core/utils';
import { GraphqlService } from '../../../services/graphql.service';
import { environment } from 'apps/web-app/src/environments/environment';
import { BatteryStatus } from "../../../models/batteryStatus";


@Component({
  selector: 'pwa-subheader',
  styleUrls: ['./subheader.component.scss'],
  templateUrl: './subheader.component.html',
  providers: [DialogService],
})
export class SubheaderComponent implements OnInit, OnDestroy {
  private destroy$: Subject<void> = new Subject<void>();
  destroySubscribeToLastStatus$: Subject<void> = new Subject<void>();
  lastMeasureTimestamp: string;

  batteryStatus: BatteryStatus;

  constructor(
    private sidebarService: NbSidebarService,
    public translate: TranslateService,
    private store: Store,
    private analytics: AnalyticsService,
    private graphqlService: GraphqlService,
    private toastrService: NbToastrService,
  ) {
  }

  /**
   * EPWA-1023 — Come nell'header desktop: l'errore delle timezone multiple arriva sia
   * come risultato con `errors` valorizzati (risposta 200) sia come eccezione
   * (fallimento di rete). Coprire solo la seconda lascerebbe muto il caso del ticket.
   */
  private notifyTimezoneError(err: unknown): void {
    if (!isMultipleTimezonesError(err) || !shouldNotifyTimezoneError()) {
      return;
    }
    this.toastrService.show(
      this.translate.instant('GRAPHQL_ERRORS.MULTIPLE_TIMEZONES'),
      this.translate.instant('COMMON.ERROR'),
      { status: 'danger', duration: TIMEZONE_TOAST_DURATION_MS },
    );
  }

  ngOnInit() {
    this.store
      .select(fromStore.selectCurrentSensorNodeSerialNumber)
      .pipe(
        skipWhile((serial) => serial == null),
        switchMap((sensorNodeSerial) =>
          this.graphqlService
            .subscribeToLastStatus({
              sensorNodeSerial: sensorNodeSerial,
            })
            .pipe(
              takeUntil(this.destroySubscribeToLastStatus$),
              bufferTime(environment.bufferTimeRefreshMS),
              filter(r => r?.length > 0),
            )
        )
      )
      .subscribe((result) => {
        let statusCheck = {};
        if (result.length > 0) {
          result.filter(r => !!r?.data?.lastStatus).map(r => r.data.lastStatus).forEach(item => {
            statusCheck['timestamp'] = item.timestamp;
            statusCheck[item.type] = item.value;
          });
          this.batteryStatus = {
            level: parseInt(statusCheck[environment.batteryLevelSensor] || '0') || 0,
            charging: statusCheck[environment.batteryInChargeSensor] === 'Y',
            date: this.getTimestampTranslated(statusCheck['timestamp']),
          }
        }
      });

    this.store
      .select(fromStore.selectCurrentSensorNodeSerialNumber)
      .pipe(
        filter((sensorNodeSerial) => sensorNodeSerial != null),
        distinctUntilChanged(),
        switchMap((sensorNodeSerial) =>
          this.store
            .select(fromStore.selectCurrentLastMeasureTimestamp)
            .pipe(
              // Al cambio centralina lo store porta ancora il timestamp della
              // precedente: quella prima emissione e' un'ancora sbagliata e va
              // scartata. `startWith(null)` fa partire subito la query col fallback,
              // poi si ri-interroga quando arriva il timestamp di QUESTA centralina.
              skip(1),
              startWith(null),
              map((lastMeasureTimestamp) => [
                sensorNodeSerial,
                lastMeasureTimestamp,
              ] as [string, string | null]),
            ),
        ),
        // EPWA-1023: stessa regola dell'header desktop — vedi il commento la' per il
        // perche' delle due emissioni per cambio centralina.
        distinctUntilChanged(
          ([serialA, tsA], [serialB, tsB]) =>
            serialA === serialB && !!tsA === !!tsB,
        ),
        switchMap(([sensorNodeSerial, lastMeasureTimestamp]) =>
          this.graphqlService
            // EPWA-1023: come nell'header desktop — finestra ancorata all'ultima misura
            // per non incappare nelle centraline con piu' timezone.
            .fetchStatusAroundLastMeasure(
              {
                input: {
                  sensorNodeSerial: sensorNodeSerial,
                  sensors: [environment.batteryInChargeSensor, environment.batteryLevelSensor],
                  aggregateByTimestamp: true,
                  limit: 2,
                },
              },
              lastMeasureTimestamp,
              {
                fetchPolicy: 'no-cache',
                // EPWA-1023: come nell'header — 'all' per ricevere anche `errors`.
                errorPolicy: 'all',
              }
            )
            .pipe(
              map((result) => {
                this.notifyTimezoneError({ graphQLErrors: result?.errors });
                const statusRows = (result?.data?.status?.data ?? result?.data?.status ?? []) as any[];
                const fetchedStatus = statusRows[0] || {};
                return {
                  batteryLevel: parseInt(fetchedStatus[environment.batteryLevelSensor]) || 0,
                  batteryInCharge: fetchedStatus[environment.batteryInChargeSensor] || 'N',
                  timestamp: fetchedStatus.timestamp || '',
                }
              }),
              catchError(err => {
                console.error(err);
                this.notifyTimezoneError(err);
                return of({batteryInCharge: 'N', batteryLevel: 0, timestamp: ''})
              })
            )
        )
      )
      .subscribe((result) => {
        this.batteryStatus = {
          level: result.batteryLevel,
          charging: result.batteryInCharge === 'Y',
          date: this.getTimestampTranslated(result.timestamp),
        }
      });

    //LastDetection
    this.store
      .select(fromStore.selectCurrentLastMeasureTimestamp)
      .pipe(takeUntil(this.destroy$))
      .subscribe((lastMeas) => {
        this.lastMeasureTimestamp = this.getTimestampTranslated(lastMeas);
      });
  }

  getTimestampTranslated(timestamp: string) {
    return DateWithTimezoneApplied(timestamp, this.translate.currentLang) ==
    'Invalid DateTime'
      ? ''
      : DateWithTimezoneApplied(timestamp, this.translate.currentLang);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.destroySubscribeToLastStatus$.next();
    this.destroySubscribeToLastStatus$.complete();
  }

  isMobile(): boolean {
    return !(document.body.offsetWidth < 1108);
  }

  getEventHeader(
    eventName: string,
    eventParams?: {
      [key: string]: any;
    }
  ) {
    this.analytics.trackEvent(eventName, eventParams);
  }
}
